import { TagOption, TagsList, TagsListItem } from '@/components/core'
import { PolarityEmoji } from '@/pages/Mentions/components/Polarity/Polarity.component'
import { BrandResponse } from '@shared/models'
import { BrandFocus, BrandRules, BrandTerms, FocusState, TermsState } from '../../types'
import { getPolarityIcon, getSpecificRuleNumber, parseRules } from '../../utils/index'
import {
  BrandBadge,
  BrandHeader,
  BrandSection,
  BrandTitle,
  CompoundEmojiContainer,
  EmojiDivider,
  EmojiGroup,
  FocusTag,
  FocusTagsList,
  NoRulesText,
  NoTermsText,
  RuleNumberSpan,
  SummaryCompanyFocusContainer,
  SummaryContainer,
  SummaryDataSourceSection,
  SummaryDataSourceTitle,
  SummaryFocusItem,
  SummaryFocusTitle,
  SummaryGrid,
  SummaryPolarityContainer,
  SummaryPolarityHeader,
  SummaryPolarityHeaderTitle,
  SummaryRuleItem,
  SummaryRulesList,
  SummaryRulesSection,
  TermFieldTitle,
  TermFieldTitleSpaced,
  TermsContainer,
} from './SummaryPreview.styles'

interface SummaryPreviewProps {
  brands: BrandResponse[]
  rulesByBrand: Record<string, BrandRules>
  termsByBrand: TermsState
  focusByBrand: FocusState
  brandCompletion: Record<string, boolean>
  excludedDataSources?: number[]
}

export function SummaryPreview({
  brands,
  rulesByBrand,
  termsByBrand,
  focusByBrand,
  brandCompletion,
  excludedDataSources = [],
}: SummaryPreviewProps): JSX.Element {
  const DATA_SOURCE_IDS = {
    SOCIAL_MEDIA: 1,
    PRESS: 2,
  } as const

  const isSocialExcluded = excludedDataSources.includes(DATA_SOURCE_IDS.SOCIAL_MEDIA)
  const isPressExcluded = excludedDataSources.includes(DATA_SOURCE_IDS.PRESS)

  const orderedBrands = [...brands].sort((a, b) =>
    Boolean(a.mainBrand) === Boolean(b.mainBrand) ? 0
    : Boolean(a.mainBrand) ? -1
    : 1,
  )

  function renderSummaryPolaritySection(
    title: string,
    specificRulesText: string,
    polarityType: string,
    dataSourceType: 'social' | 'press',
    termsState: BrandTerms,
  ): JSX.Element {
    const specificRules = parseRules(specificRulesText)

    const getTermsForSummary = () => {
      if (dataSourceType === 'social') {
        const publishersTags =
          polarityType === 'POSITIVO' ? termsState.socialPositivePublishersTags
          : polarityType === 'NEGATIVO' ? termsState.socialNegativePublishersTags
          : polarityType === 'FAVORÁVEL' ? termsState.socialFavorablePublishersTags
          : termsState.socialUnfavorablePublishersTags

        const proprietaryTags =
          polarityType === 'POSITIVO' ? termsState.socialPositiveProprietaryTags
          : polarityType === 'NEGATIVO' ? termsState.socialNegativeProprietaryTags
          : polarityType === 'FAVORÁVEL' ? termsState.socialFavorableProprietaryTags
          : termsState.socialUnfavorableProprietaryTags

        return { publishersTags, proprietaryTags, channelsTags: [] }
      } else {
        const channelsTags =
          polarityType === 'POSITIVO' ? termsState.pressPositiveChannelsTags
          : polarityType === 'NEGATIVO' ? termsState.pressNegativeChannelsTags
          : polarityType === 'FAVORÁVEL' ? termsState.pressFavorableChannelsTags
          : termsState.pressUnfavorableChannelsTags

        const proprietaryTags =
          polarityType === 'POSITIVO' ? termsState.pressPositiveProprietaryTags
          : polarityType === 'NEGATIVO' ? termsState.pressNegativeProprietaryTags
          : polarityType === 'FAVORÁVEL' ? termsState.pressFavorableProprietaryTags
          : termsState.pressUnfavorableProprietaryTags

        return { channelsTags, proprietaryTags, publishersTags: [] }
      }
    }

    const { publishersTags, channelsTags, proprietaryTags } = getTermsForSummary()

    return (
      <SummaryPolarityContainer>
        <SummaryPolarityHeader>
          <PolarityEmoji value={getPolarityIcon(title)} />
          <SummaryPolarityHeaderTitle>{title}</SummaryPolarityHeaderTitle>
        </SummaryPolarityHeader>

        <SummaryRulesSection>
          <h5>Termos:</h5>
          <TermsContainer>
            <TermFieldTitle>{dataSourceType === 'social' ? 'Publicadores' : 'Veículos'}:</TermFieldTitle>
            {(dataSourceType === 'social' ? publishersTags : channelsTags).length > 0 ?
              <TagsList
                maxVisible={10}
                tags={(dataSourceType === 'social' ? publishersTags : channelsTags).map(
                  (tag) =>
                    ({
                      id: tag.id,
                      text: tag.text,
                    }) as TagsListItem,
                )}
                variant="preview"
                emptyText="Nenhum definido"
              />
            : <NoTermsText>Nenhum definido</NoTermsText>}

            <TermFieldTitleSpaced>Termos Proprietários:</TermFieldTitleSpaced>
            {proprietaryTags.length > 0 ?
              <TagsList
                maxVisible={10}
                tags={proprietaryTags.map(
                  (tag) =>
                    ({
                      id: tag.id,
                      text: tag.text,
                    }) as TagsListItem,
                )}
                variant="preview"
                emptyText="Nenhum definido"
              />
            : <NoTermsText>Nenhum definido</NoTermsText>}
          </TermsContainer>
        </SummaryRulesSection>

        <SummaryRulesSection>
          <h5>Premissas Específicas:</h5>
          {specificRules.length > 0 ?
            <SummaryRulesList>
              {specificRules.map((rule, index) => (
                <SummaryRuleItem key={`specific-${index}`}>
                  <RuleNumberSpan>{getSpecificRuleNumber(polarityType, index)}.</RuleNumberSpan>
                  <span>{rule}</span>
                </SummaryRuleItem>
              ))}
            </SummaryRulesList>
          : <NoRulesText>Nenhuma premissa específica definida</NoRulesText>}
        </SummaryRulesSection>
      </SummaryPolarityContainer>
    )
  }

  function renderCompoundPolaritySection(
    title: string,
    polarity: 'POSITIVO_FAVORAVEL' | 'NEGATIVO_DESFAVORAVEL',
    dataSourceType: 'social' | 'press',
    termsState: BrandTerms,
  ): JSX.Element {
    const getCompoundTermsForSummary = () => {
      if (dataSourceType === 'social') {
        const publishersTags =
          polarity === 'POSITIVO_FAVORAVEL' ?
            termsState.socialPositiveFavorablePublishersTags
          : termsState.socialNegativeUnfavorablePublishersTags

        const proprietaryTags =
          polarity === 'POSITIVO_FAVORAVEL' ?
            termsState.socialPositiveFavorableProprietaryTags
          : termsState.socialNegativeUnfavorableProprietaryTags

        return { publishersTags, proprietaryTags, channelsTags: [] }
      } else {
        const channelsTags =
          polarity === 'POSITIVO_FAVORAVEL' ?
            termsState.pressPositiveFavorableChannelsTags
          : termsState.pressNegativeUnfavorableChannelsTags

        const proprietaryTags =
          polarity === 'POSITIVO_FAVORAVEL' ?
            termsState.pressPositiveFavorableProprietaryTags
          : termsState.pressNegativeUnfavorableProprietaryTags

        return { channelsTags, proprietaryTags, publishersTags: [] }
      }
    }

    const { publishersTags, channelsTags, proprietaryTags } = getCompoundTermsForSummary()

    const getCompoundEmoji = () => {
      if (title === 'POSITIVO / FAVORÁVEL') {
        return (
          <CompoundEmojiContainer>
            <EmojiGroup>
              <PolarityEmoji value={getPolarityIcon('POSITIVO')} />
              <SummaryPolarityHeaderTitle>POSITIVO</SummaryPolarityHeaderTitle>
            </EmojiGroup>
            <EmojiDivider>/</EmojiDivider>
            <EmojiGroup>
              <PolarityEmoji value={getPolarityIcon('FAVORÁVEL')} />
              <SummaryPolarityHeaderTitle>FAVORÁVEL</SummaryPolarityHeaderTitle>
            </EmojiGroup>
          </CompoundEmojiContainer>
        )
      } else {
        return (
          <CompoundEmojiContainer>
            <EmojiGroup>
              <PolarityEmoji value={getPolarityIcon('NEGATIVO')} />
              <SummaryPolarityHeaderTitle>NEGATIVO</SummaryPolarityHeaderTitle>
            </EmojiGroup>
            <EmojiDivider>/</EmojiDivider>
            <EmojiGroup>
              <PolarityEmoji value={getPolarityIcon('DESFAVORÁVEL')} />
              <SummaryPolarityHeaderTitle>DESFAVORÁVEL</SummaryPolarityHeaderTitle>
            </EmojiGroup>
          </CompoundEmojiContainer>
        )
      }
    }

    return (
      <SummaryPolarityContainer>
        <SummaryPolarityHeader>
          <SummaryPolarityHeaderTitle>{getCompoundEmoji()}</SummaryPolarityHeaderTitle>
        </SummaryPolarityHeader>

        <SummaryRulesSection>
          <h5>Termos:</h5>
          <TermsContainer>
            <TermFieldTitle>{dataSourceType === 'social' ? 'Publicadores' : 'Veículos'}:</TermFieldTitle>
            {(dataSourceType === 'social' ? publishersTags : channelsTags).length > 0 ?
              <TagsList
                maxVisible={10}
                tags={(dataSourceType === 'social' ? publishersTags : channelsTags).map(
                  (tag) =>
                    ({
                      id: tag.id,
                      text: tag.text,
                    }) as TagsListItem,
                )}
                variant="preview"
                emptyText="Nenhum definido"
              />
            : <NoTermsText>Nenhum definido</NoTermsText>}

            <TermFieldTitleSpaced>Termos Proprietários:</TermFieldTitleSpaced>
            {proprietaryTags.length > 0 ?
              <TagsList
                maxVisible={10}
                tags={proprietaryTags.map(
                  (tag) =>
                    ({
                      id: tag.id,
                      text: tag.text,
                    }) as TagsListItem,
                )}
                variant="preview"
                emptyText="Nenhum definido"
              />
            : <NoTermsText>Nenhum definido</NoTermsText>}
          </TermsContainer>
        </SummaryRulesSection>
      </SummaryPolarityContainer>
    )
  }

  function renderNeutralPolaritySection(title: string, rulesText: string): JSX.Element {
    const rules = parseRules(rulesText)
    return (
      <SummaryPolarityContainer>
        <SummaryPolarityHeader>
          <PolarityEmoji value={getPolarityIcon('NEUTRO')} />
          <h4>{title}</h4>
        </SummaryPolarityHeader>
        <SummaryRulesSection>
          <h5>Premissas:</h5>
          {rules.length > 0 ?
            <SummaryRulesList>
              {rules.map((rule, index) => (
                <SummaryRuleItem key={`neutral-${index}`}>
                  <span
                    style={{ fontSize: '14px', fontWeight: '600', color: '#9CA3AF', flexShrink: 0, minWidth: '20px' }}>
                    {getSpecificRuleNumber('NEUTRO', index)}.
                  </span>
                  <span>{rule}</span>
                </SummaryRuleItem>
              ))}
            </SummaryRulesList>
          : <p>Nenhuma premissa definida</p>}
        </SummaryRulesSection>
      </SummaryPolarityContainer>
    )
  }

  function getEmptyRules(): BrandRules {
    return {
      social: {
        positiveGeneral: '',
        positiveSpecific: '',
        negativeGeneral: '',
        negativeSpecific: '',
        favorableGeneral: '',
        favorableSpecific: '',
        unfavorableGeneral: '',
        unfavorableSpecific: '',
        neutralRules: '',
      },
      press: {
        positiveGeneral: '',
        positiveSpecific: '',
        negativeGeneral: '',
        negativeSpecific: '',
        favorableGeneral: '',
        favorableSpecific: '',
        unfavorableGeneral: '',
        unfavorableSpecific: '',
        neutralRules: '',
      },
    }
  }

  function getEmptyTerms(): BrandTerms {
    return {
      socialPositivePublishersTags: [],
      socialNegativePublishersTags: [],
      socialFavorablePublishersTags: [],
      socialUnfavorablePublishersTags: [],
      socialPositiveProprietaryTags: [],
      socialNegativeProprietaryTags: [],
      socialFavorableProprietaryTags: [],
      socialUnfavorableProprietaryTags: [],
      pressPositiveChannelsTags: [],
      pressNegativeChannelsTags: [],
      pressFavorableChannelsTags: [],
      pressUnfavorableChannelsTags: [],
      pressPositiveProprietaryTags: [],
      pressNegativeProprietaryTags: [],
      pressFavorableProprietaryTags: [],
      pressUnfavorableProprietaryTags: [],
      socialPositiveFavorablePublishersTags: [],
      socialPositiveFavorableProprietaryTags: [],
      socialNegativeUnfavorablePublishersTags: [],
      socialNegativeUnfavorableProprietaryTags: [],
      pressPositiveFavorableChannelsTags: [],
      pressPositiveFavorableProprietaryTags: [],
      pressNegativeUnfavorableChannelsTags: [],
      pressNegativeUnfavorableProprietaryTags: [],
    }
  }

  function getEmptyFocus(): BrandFocus {
    return {
      socialClientFocusTags: [],
      socialCompetitorFocusTags: [],
      pressClientFocusTags: [],
      pressCompetitorFocusTags: [],
    }
  }

  return (
    <SummaryContainer>
      {orderedBrands.map((brand) => {
        const brandRules = rulesByBrand[brand.id] || getEmptyRules()
        const brandTerms = termsByBrand[brand.id] || getEmptyTerms()
        const brandFocus = focusByBrand[brand.id] || getEmptyFocus()

        return (
          <BrandSection key={brand.id}>
            <BrandHeader>
              <BrandTitle>{brand.name}</BrandTitle>
              <BrandBadge $success={brandCompletion[brand.id] ?? false}>
                {(brandCompletion[brand.id] ?? false) ? 'Completo' : 'Incompleto'}
              </BrandBadge>
            </BrandHeader>

            {!isSocialExcluded && (
              <SummaryDataSourceSection>
                <SummaryDataSourceTitle>Redes Sociais</SummaryDataSourceTitle>

                <SummaryCompanyFocusContainer>
                  <SummaryFocusItem>
                    <SummaryFocusTitle>Foco Cliente</SummaryFocusTitle>
                    {brandFocus.socialClientFocusTags.length > 0 ?
                      <FocusTagsList>
                        {brandFocus.socialClientFocusTags.map((tag: TagOption) => (
                          <FocusTag key={tag.id}>{tag.text}</FocusTag>
                        ))}
                      </FocusTagsList>
                    : <NoRulesText>Nenhum foco cliente definido</NoRulesText>}
                  </SummaryFocusItem>
                  <SummaryFocusItem>
                    <SummaryFocusTitle>Foco Concorrente</SummaryFocusTitle>
                    {brandFocus.socialCompetitorFocusTags.length > 0 ?
                      <FocusTagsList>
                        {brandFocus.socialCompetitorFocusTags.map((tag: TagOption) => (
                          <FocusTag key={tag.id}>{tag.text}</FocusTag>
                        ))}
                      </FocusTagsList>
                    : <NoRulesText>Nenhum foco concorrente definido</NoRulesText>}
                  </SummaryFocusItem>
                </SummaryCompanyFocusContainer>

                <SummaryGrid>
                  {renderSummaryPolaritySection(
                    'POSITIVO',
                    brandRules.social.positiveSpecific,
                    'POSITIVO',
                    'social',
                    brandTerms,
                  )}
                  {renderSummaryPolaritySection(
                    'NEGATIVO',
                    brandRules.social.negativeSpecific,
                    'NEGATIVO',
                    'social',
                    brandTerms,
                  )}
                </SummaryGrid>
                <SummaryGrid>
                  {renderSummaryPolaritySection(
                    'FAVORÁVEL',
                    brandRules.social.favorableSpecific,
                    'FAVORÁVEL',
                    'social',
                    brandTerms,
                  )}
                  {renderSummaryPolaritySection(
                    'DESFAVORÁVEL',
                    brandRules.social.unfavorableSpecific,
                    'DESFAVORÁVEL',
                    'social',
                    brandTerms,
                  )}
                </SummaryGrid>
                <SummaryGrid>
                  {renderCompoundPolaritySection('POSITIVO / FAVORÁVEL', 'POSITIVO_FAVORAVEL', 'social', brandTerms)}
                  {renderCompoundPolaritySection(
                    'NEGATIVO / DESFAVORÁVEL',
                    'NEGATIVO_DESFAVORAVEL',
                    'social',
                    brandTerms,
                  )}
                </SummaryGrid>
                <SummaryGrid>{renderNeutralPolaritySection('NEUTRO', brandRules.social.neutralRules)}</SummaryGrid>
              </SummaryDataSourceSection>
            )}

            {!isPressExcluded && (
              <SummaryDataSourceSection>
                <SummaryDataSourceTitle>Imprensa</SummaryDataSourceTitle>

                <SummaryCompanyFocusContainer>
                  <SummaryFocusItem>
                    <SummaryFocusTitle>Foco Cliente</SummaryFocusTitle>
                    {brandFocus.pressClientFocusTags.length > 0 ?
                      <FocusTagsList>
                        {brandFocus.pressClientFocusTags.map((tag: TagOption) => (
                          <FocusTag key={tag.id}>{tag.text}</FocusTag>
                        ))}
                      </FocusTagsList>
                    : <NoRulesText>Nenhum foco cliente definido</NoRulesText>}
                  </SummaryFocusItem>
                  <SummaryFocusItem>
                    <SummaryFocusTitle>Foco Concorrente</SummaryFocusTitle>
                    {brandFocus.pressCompetitorFocusTags.length > 0 ?
                      <FocusTagsList>
                        {brandFocus.pressCompetitorFocusTags.map((tag: TagOption) => (
                          <FocusTag key={tag.id}>{tag.text}</FocusTag>
                        ))}
                      </FocusTagsList>
                    : <NoRulesText>Nenhum foco concorrente definido</NoRulesText>}
                  </SummaryFocusItem>
                </SummaryCompanyFocusContainer>

                <SummaryGrid>
                  {renderSummaryPolaritySection(
                    'POSITIVO',
                    brandRules.press.positiveSpecific,
                    'POSITIVO',
                    'press',
                    brandTerms,
                  )}
                  {renderSummaryPolaritySection(
                    'NEGATIVO',
                    brandRules.press.negativeSpecific,
                    'NEGATIVO',
                    'press',
                    brandTerms,
                  )}
                </SummaryGrid>
                <SummaryGrid>
                  {renderSummaryPolaritySection(
                    'FAVORÁVEL',
                    brandRules.press.favorableSpecific,
                    'FAVORÁVEL',
                    'press',
                    brandTerms,
                  )}
                  {renderSummaryPolaritySection(
                    'DESFAVORÁVEL',
                    brandRules.press.unfavorableSpecific,
                    'DESFAVORÁVEL',
                    'press',
                    brandTerms,
                  )}
                </SummaryGrid>
                <SummaryGrid>
                  {renderCompoundPolaritySection('POSITIVO / FAVORÁVEL', 'POSITIVO_FAVORAVEL', 'press', brandTerms)}
                  {renderCompoundPolaritySection(
                    'NEGATIVO / DESFAVORÁVEL',
                    'NEGATIVO_DESFAVORAVEL',
                    'press',
                    brandTerms,
                  )}
                </SummaryGrid>
                <SummaryGrid>{renderNeutralPolaritySection('NEUTRO', brandRules.press.neutralRules)}</SummaryGrid>
              </SummaryDataSourceSection>
            )}
          </BrandSection>
        )
      })}
    </SummaryContainer>
  )
}
